import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Layers3, Compass, Activity } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import NotFound from "@/pages/NotFound";
import { useLang } from "@/i18n/LanguageContext";
import { CAPABILITIES } from "@/lib/dataleo";
import { serviceSlug } from "@/lib/servicePaths";
import { SERVICE_DETAILS } from "@/lib/serviceDetails";
import { radarLinkFor } from "@/lib/radarLinks";

/**
 * Commercial service page. The business offer is the primary experience:
 * Radar topics are related reading, never a replacement for an offer.
 * Data is derived from the existing bilingual capability taxonomy.
 */
const FRAMEWORK = {
  advisory: {
    challenge: {
      en: "Planning choices are often made across disconnected functions, assumptions and data. Teams need shared decision rights, a practical operating model and a delivery roadmap.",
      fr: "Les décisions de planification reposent souvent sur des fonctions, des hypothèses et des données cloisonnées. Les équipes ont besoin de responsabilités claires, d'un modèle opérationnel et d'une feuille de route réaliste.",
    },
    approach: {
      en: ["Clarify the business decisions and stakeholders", "Assess processes, governance, data and existing systems", "Define the target operating model and options", "Prioritize a realistic transformation roadmap"],
      fr: ["Clarifier les décisions métier et les parties prenantes", "Évaluer processus, gouvernance, données et systèmes", "Définir le modèle cible et les options", "Prioriser une feuille de route de transformation réaliste"],
    },
    outputs: {
      en: ["Current-state assessment", "Decision and governance framework", "Target process and operating model", "Prioritized transformation roadmap"],
      fr: ["Diagnostic de l'existant", "Cadre de décision et de gouvernance", "Processus et modèle opérationnel cibles", "Feuille de route de transformation priorisée"],
    },
  },
  delivery: {
    challenge: {
      en: "Planning technology programs need more than system configuration. Their success depends on business ownership, integration, testing, adoption and a sustainable operating model.",
      fr: "Un programme de technologie de planification ne se limite pas au paramétrage d'un outil. Sa réussite dépend du pilotage métier, de l'intégration, des tests, de l'adoption et du modèle de fonctionnement.",
    },
    approach: {
      en: ["Frame scope, business requirements and program governance", "Align functional design, data and integration workstreams", "Coordinate delivery, validation, cutover and adoption", "Stabilize operations and structure continuous improvement"],
      fr: ["Cadrer le périmètre, les besoins et la gouvernance", "Aligner conception fonctionnelle, données et intégration", "Coordonner réalisation, validation, bascule et adoption", "Stabiliser les opérations et organiser l'amélioration continue"],
    },
    outputs: {
      en: ["Solution and delivery roadmap", "Functional and integration requirements", "Test, deployment and cutover plans", "Operational support and improvement backlog"],
      fr: ["Feuille de route solution et déploiement", "Exigences fonctionnelles et d'intégration", "Plans de tests, déploiement et bascule", "Organisation du support et backlog d'amélioration"],
    },
  },
  "ai-build": {
    challenge: {
      en: "AI prototypes create value only when their data, workflow, controls and human decisions are connected to the reality of enterprise operations.",
      fr: "Les prototypes IA ne créent de valeur que lorsque leurs données, workflows, contrôles et décisions humaines s'intègrent aux opérations réelles de l'entreprise.",
    },
    approach: {
      en: ["Identify a bounded decision and an accountable owner", "Assess available data, integrations and risks", "Prototype with explicit assumptions and evaluation criteria", "Validate outcomes, controls and potential industrialization"],
      fr: ["Identifier une décision ciblée et son responsable", "Évaluer données, intégrations et risques", "Prototyper avec des hypothèses et critères explicites", "Valider résultats, contrôles et conditions d'industrialisation"],
    },
    outputs: {
      en: ["Use-case and feasibility assessment", "Decision workflow and architecture", "Prototype or proof-of-concept specification", "Validation criteria and deployment options"],
      fr: ["Évaluation du cas d'usage et de sa faisabilité", "Workflow décisionnel et architecture", "Prototype ou cahier des charges du POC", "Critères de validation et options de déploiement"],
    },
  },
};

const Section = ({ eyebrow, heading, children, dark = false }) => (
  <section className={dark ? "bg-[#10231d] text-white" : "bg-[#F6F3EE] text-[#0B1210]"}>
    <div className="dl-container py-14 sm:py-20">
      <p className="font-mono-dl text-[11px] tracking-[0.18em] uppercase text-[#00A878]">{eyebrow}</p>
      <h2 className="font-display text-3xl sm:text-4xl leading-tight mt-3 mb-8 max-w-3xl">{heading}</h2>
      {children}
    </div>
  </section>
);

export default function ServiceDetail() {
  const { slug, service } = useParams();
  const { lang } = useLang();
  const pillar = CAPABILITIES.find((p) => p.slug === slug);
  const cluster = pillar?.clusters.find((c) => c.items.some((item) => serviceSlug(item) === service));
  const label = cluster?.items.find((item) => serviceSlug(item) === service);
  if (!pillar || !cluster || !label) return <NotFound />;

  const fr = lang === "fr";
  const framework = FRAMEWORK[slug];
  const profile = SERVICE_DETAILS[service];
  const pillarName = pillar.title[lang];
  const intro = profile?.intro?.[lang] || (slug === "delivery" && cluster.code === "01"
    ? (fr ? `Notre expertise couvre les projets de planification utilisant ${label} : cadrage des besoins métier, gouvernance du déploiement, intégration et adoption. Toute référence à une technologie ne constitue pas une revendication de partenariat officiel.` : `Our planning technology experience includes projects involving ${label}: business requirements, delivery governance, integration and adoption. Technology experience does not imply an official vendor partnership.`)
    : framework.challenge[lang]);

  return (
    <div data-testid="service-detail" className="bg-[#F6F3EE]">
      <SeoHead
        lang={lang}
        path={`/capabilities/${slug}/${service}`}
        bareTitle
        title={`${label} | ${pillarName} | Dataleo`}
        description={intro}
      />
      <section className="bg-[#10231d] text-white">
        <div className="dl-container pt-10 pb-16 sm:pb-24">
          <nav aria-label="Breadcrumb" className="text-xs text-white/65 flex flex-wrap items-center gap-2">
            <Link to="/capabilities" className="hover:text-white">{fr ? "Capacités" : "Capabilities"}</Link>
            <span aria-hidden="true">/</span>
            <Link to={`/capabilities/${slug}`} className="hover:text-white">{pillarName}</Link>
            <span aria-hidden="true">/</span><span className="text-white">{label}</span>
          </nav>
          <div className="max-w-4xl mt-16">
            <p className="font-mono-dl text-xs text-[#00D7A1] uppercase tracking-[0.2em]">{cluster.name[lang]}</p>
            <h1 className="font-display text-4xl sm:text-6xl leading-[1.05] mt-5">{label}</h1>
            <p className="text-lg sm:text-xl text-white/75 leading-relaxed mt-7 max-w-3xl">{intro}</p>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link className="dl-pill dl-pill-primary" to={`/contact?service=${encodeURIComponent(label)}`}>
                {fr ? "Échanger sur ce besoin" : "Discuss this challenge"} <ArrowRight size={16} />
              </Link>
              <Link className="dl-pill dl-pill-outline !text-white !border-white/30" to={`/capabilities/${slug}`}>
                {fr ? "Voir l'expertise" : "Explore the capability"}
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Section eyebrow={fr ? "Ce que nous apportons" : "What we deliver"} heading={fr ? "Des décisions, des processus et des livrables concrets." : "Concrete decisions, processes and deliverables."}>
        <p className="text-[#4B5563] leading-relaxed max-w-3xl mb-8">
          {fr ? "Nous adaptons le périmètre à la situation et aux contraintes de votre organisation. Les livrables ci-dessous sont des exemples de formats de mission, pas des engagements standardisés." : "We tailor the engagement to your operating context. The deliverables below illustrate possible work products rather than a fixed package or guaranteed outcome."}
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {(profile?.deliverables?.[lang] || framework.outputs[lang]).map((item) => (
            <div key={item} className="flex gap-3 p-5 rounded-2xl bg-white border border-[#E6E0D5]">
              <CheckCircle2 size={20} className="text-[#00A878] shrink-0 mt-0.5" />
              <span className="font-bold-dl text-[15px]">{item}</span>
            </div>
          ))}
        </div>
      </Section>
      <Section dark eyebrow={fr ? "Notre approche" : "Our approach"} heading={fr ? "Du cadrage à la mise en pratique." : "From framing to practical execution."}>
        <div className="grid md:grid-cols-4 gap-5">
          {framework.approach[lang].map((item, i) => (
            <div key={item} className="border-t border-white/30 pt-5">
              <span className="font-mono-dl text-sm text-[#00D7A1]">0{i + 1}</span>
              <p className="mt-4 text-[15px] leading-relaxed text-white/85">{item}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section eyebrow={fr ? "Votre contexte" : "Your context"} heading={fr ? "Une approche adaptée à votre organisation." : "An approach tailored to your organization."}>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { Icon: Compass, en: "Business outcomes first", fr: "Les enjeux métier d'abord" },
            { Icon: Layers3, en: "Integrated processes and systems", fr: "Processus et systèmes intégrés" },
            { Icon: Activity, en: "Human accountability throughout", fr: "Responsabilité humaine à chaque étape" },
          ].map(({ Icon, en, fr: frText }) => (
            <div key={en} className="bg-white rounded-2xl border border-[#E6E0D5] p-7">
              <Icon className="text-[#00A878]" size={27} />
              <h3 className="font-display text-2xl mt-5">{fr ? frText : en}</h3>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-5 mt-10">
          <Link to={`/contact?service=${encodeURIComponent(label)}`} className="dl-pill dl-pill-dark">
            {fr ? "Parler de votre projet" : "Discuss your project"} <ArrowRight size={16} />
          </Link>
          <Link to={radarLinkFor(label).to} className="text-[#166B55] underline underline-offset-4 text-sm font-bold-dl">
            {fr ? "Explorer les analyses Radar associées" : "Read related Radar intelligence"}
          </Link>
        </div>
      </Section>
    </div>
  );
}
